import { DateTime } from 'luxon';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import ScheduleReminderType from 'types/enum/ScheduleReminderType';
import { getRangeTimeFromDate } from 'utils/dateUtils';
import { executeFunctionsCurried } from 'utils/functionUtils';
import { defaultSchedulingColors } from '../constants/constants';
import { useScheduling } from '../hooks';
import { SchedulingEventType } from '../types/types';
import { getSchedulingTypeFromString } from '../utils/utils';
import { useSchedulingTimeOffDataState } from './SchedulingTimeOffDataContext';

export type ISchedulingDataContextState = ReturnType<typeof useSchedulingDataProperties>;

const SchedulingDataContext = createContext<ISchedulingDataContextState | null>(null);

export function useSchedulingData() {
  const context = useContext(SchedulingDataContext);

  if (context === null || context === undefined) {
    throw new Error('useSchedulingData must be nested within a SchedulingDataContext.Provider.');
  }

  return context;
}

interface ISchedulingDataContextProviderProps {
  children: ReactNode;
}

export function SchedulingDataContextProvider({ children }: ISchedulingDataContextProviderProps) {
  return (
    <SchedulingDataContext.Provider value={useSchedulingDataProperties()}>{children}</SchedulingDataContext.Provider>
  );
}

function useSchedulingDataProperties() {
  const { reminderDetails, ...schedulingProperties } = useScheduling();
  const [events, setEvents] = useState<SchedulingEventType[]>([]);
  const { events: timeOffEvents, ...timeOffProperties } = useSchedulingTimeOffDataState();

  useEffect(() => {
    async function getData() {
      const schedulingEvents = reminderDetails.map((schedule) => {
        const start = DateTime.fromISO(schedule.startTime).toJSDate();
        const end = DateTime.fromISO(schedule.endTime).toJSDate();

        const title = getRangeTimeFromDate(start, end, 't', 'system');

        let color: string = schedule.color || '';
        if (!color) {
          if (schedule.type === 'work') {
            color = defaultSchedulingColors.work;
          } else {
            color = defaultSchedulingColors.break;
          }
        }

        return {
          ...schedule,
          id: schedule.id,
          reminder: schedule,
          title,
          members: schedule.links.map((link) => link.member),
          type: getSchedulingTypeFromString(schedule.type),
          start,
          end,
          allDay: false,
          color,
          instructions: schedule.instructions,
        };
      });

      setEvents(schedulingEvents);
    }

    getData();
  }, [reminderDetails]);

  const allEvents = useMemo(() => [...events, ...timeOffEvents], [events, timeOffEvents]);

  return useMemo(
    () => ({
      events: allEvents,
      reminderDetails,
      ...schedulingProperties,
      loading: schedulingProperties.loading || timeOffProperties.loading,
      error: schedulingProperties.error || timeOffProperties.error,
      refetch: executeFunctionsCurried(schedulingProperties.refetch, timeOffProperties.execute),
      refetchTimeOffs: timeOffProperties.execute,
      refetchScheduleReminders: schedulingProperties.refetch,
    }),
    [
      allEvents,
      reminderDetails,
      schedulingProperties,
      timeOffProperties.error,
      timeOffProperties.execute,
      timeOffProperties.loading,
    ]
  );
}

export default SchedulingDataContext;
